import React, { useState, useEffect } from "react";
import { TextField } from "../../../../ui/components/TextField";
import { Button } from "../../../../ui/components/Button";
import { Select } from "../../../../ui/components/Select";
import {
  getGstinVerificationSettings,
  saveGstinVerificationSettings,
} from "../../../../shared/data/api/gstinVerificationApi";
import { useToast } from "../../../../shared/presentation/components";

const GstinVerificationSettings: React.FC = () => {
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [verificationRequired, setVerificationRequired] = useState("Y");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { showSuccess, showError } = useToast();

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const settings = await getGstinVerificationSettings();
      setClientId(settings.cashfree_client_id || "");
      setClientSecret(settings.cashfree_client_secret || "");
      setVerificationRequired(settings.gstin_verification_required || "Y");
    } catch (err) {
      console.error("Failed to load GSTIN verification settings:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await saveGstinVerificationSettings({
        cashfree_client_id: clientId,
        cashfree_client_secret: clientSecret,
        gstin_verification_required: verificationRequired,
      });
      showSuccess("GSTIN verification settings saved successfully");
    } catch (err) {
      console.error("Failed to save settings:", err);
      showError("Failed to save GSTIN verification settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <span className="text-neutral-500">Loading settings...</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 p-6 bg-white rounded-lg border border-neutral-200">
      <div>
        <h3 className="text-lg font-semibold text-default-font">
          GSTIN Verification Settings
        </h3>
        <p className="text-sm text-neutral-500 mt-1">
          Configure Cashfree credentials for GSTIN verification. When enabled,
          vendors and customers must have their GSTIN verified before saving.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <TextField label="Cashfree Client ID">
          <TextField.Input
            placeholder="Enter Cashfree Client ID"
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
          />
        </TextField>

        <TextField label="Cashfree Client Secret">
          <TextField.Input
            type="password"
            placeholder="Enter Cashfree Client Secret"
            value={clientSecret}
            onChange={(e) => setClientSecret(e.target.value)}
          />
        </TextField>
      </div>

      <div className="w-64">
        <Select
          label="GSTIN Verification Required"
          value={verificationRequired}
          onValueChange={(value) => setVerificationRequired(value)}
        >
          <Select.Item value="Y">Yes - Require verification</Select.Item>
          <Select.Item value="N">No - Skip verification</Select.Item>
        </Select>
      </div>

      <div>
        <Button
          variant="brand-primary"
          size="medium"
          onClick={handleSave}
          disabled={saving}
          loading={saving}
        >
          {saving ? "Saving..." : "Save Settings"}
        </Button>
      </div>
    </div>
  );
};

export default GstinVerificationSettings;
